export interface IDesignFingerprint {
	pHash	: string;
	flyId	: string;
}

export interface IDuplicateFingerprintParams {
	designs				: IDesignFingerprint[];
	maxHammingDistance	: number;
}

interface IHashLengthIndex {
	segmentCount			: number;
	groupIndexes			: number[];
	groupIndexesBySegment	: Array<Map<string, number[]>>;
}


/**------------------------------------------------------
 * Duplicate Fingerprint Grouper
 * -----------------------------
 * Uses the Hamming-distance pigeonhole principle to find
 * exact candidates without comparing every fingerprint
 * against every existing group representative.
 */
export class DuplicateFingerprintGrouper {

	findDuplicateFlyIds(designs: IDesignFingerprint[], maxHammingDistance: number): string[] {
		const normalizedDistance: number = Math.ceil(Number(maxHammingDistance));
		if (designs.length < 2 || normalizedDistance <= 0) return [];

		const groups			: Array<IDesignFingerprint[]> = [];
		const indexesByHashLength	: Map<number, IHashLengthIndex> = new Map<number, IHashLengthIndex>();

		for (const design of designs) {
			if (!design?.pHash || !design.flyId) continue;

			const hashLengthIndex: IHashLengthIndex = this.getHashLengthIndex(
				indexesByHashLength,
				design.pHash.length,
				normalizedDistance
			);
			const candidateGroupIndexes: number[] = this.getCandidateGroupIndexes(
				design.pHash,
				hashLengthIndex,
				normalizedDistance
			);
			let matchingGroupIndex: number = -1;
			for (const groupIndex of candidateGroupIndexes) {
				if (!this.isWithinHammingDistance(
					groups[groupIndex][0].pHash,
					design.pHash,
					normalizedDistance
				)) continue;
				matchingGroupIndex = groupIndex;
				break;
			}

			if (matchingGroupIndex >= 0) {
				groups[matchingGroupIndex].push(design);
				continue;
			}

			const newGroupIndex: number = groups.length;
			groups.push([design]);
			this.indexGroupRepresentative(design.pHash, newGroupIndex, hashLengthIndex);
		}

		const duplicateGroups: Array<IDesignFingerprint[]> = groups.filter((group: IDesignFingerprint[]) => {
			return group.length > 1;
		});
		duplicateGroups.sort((a: IDesignFingerprint[], b: IDesignFingerprint[]) => a.length - b.length);
		duplicateGroups.reverse();

		const duplicateFlyIds: string[] = [];
		for (const group of duplicateGroups) {
			for (const design of group) duplicateFlyIds.push(design.flyId);
		}
		return duplicateFlyIds;
	}

	private getHashLengthIndex(
		indexesByHashLength: Map<number, IHashLengthIndex>,
		hashLength: number,
		maxHammingDistance: number
	): IHashLengthIndex {
		const existingIndex = indexesByHashLength.get(hashLength);
		if (existingIndex) return existingIndex;

		const segmentCount: number = Math.max(1, Math.min(hashLength, maxHammingDistance));
		const newIndex: IHashLengthIndex = {
			segmentCount,
			groupIndexes			: [],
			groupIndexesBySegment	: Array.from(
				{ length: segmentCount },
				() => new Map<string, number[]>()
			)
		};
		indexesByHashLength.set(hashLength, newIndex);
		return newIndex;
	}

	private getCandidateGroupIndexes(
		pHash: string,
		hashLengthIndex: IHashLengthIndex,
		maxHammingDistance: number
	): number[] {
		if (maxHammingDistance > pHash.length) return [...hashLengthIndex.groupIndexes];

		const candidateGroupIndexes: Set<number> = new Set<number>();
		for (let segmentIndex: number = 0; segmentIndex < hashLengthIndex.segmentCount; segmentIndex++) {
			const segment: string = this.getSegment(pHash, segmentIndex, hashLengthIndex.segmentCount);
			const indexedGroups: number[] = hashLengthIndex.groupIndexesBySegment[segmentIndex].get(segment) || [];
			for (const groupIndex of indexedGroups) candidateGroupIndexes.add(groupIndex);
		}
		return [...candidateGroupIndexes].sort((a: number, b: number) => a - b);
	}

	private indexGroupRepresentative(
		pHash: string,
		groupIndex: number,
		hashLengthIndex: IHashLengthIndex
	): void {
		hashLengthIndex.groupIndexes.push(groupIndex);
		for (let segmentIndex: number = 0; segmentIndex < hashLengthIndex.segmentCount; segmentIndex++) {
			const segment: string = this.getSegment(pHash, segmentIndex, hashLengthIndex.segmentCount);
			const segmentGroups: number[] = hashLengthIndex.groupIndexesBySegment[segmentIndex].get(segment) || [];
			segmentGroups.push(groupIndex);
			hashLengthIndex.groupIndexesBySegment[segmentIndex].set(segment, segmentGroups);
		}
	}

	private getSegment(pHash: string, segmentIndex: number, segmentCount: number): string {
		const startIndex: number = Math.floor((segmentIndex * pHash.length) / segmentCount);
		const endIndex: number = Math.floor(((segmentIndex + 1) * pHash.length) / segmentCount);
		return pHash.slice(startIndex, endIndex);
	}

	private isWithinHammingDistance(a: string, b: string, maxHammingDistance: number): boolean {
		if (a.length !== b.length) return false;

		let distance: number = 0;
		for (let index: number = 0; index < a.length; index++) {
			if (a[index] === b[index]) continue;
			distance++;
			if (distance >= maxHammingDistance) return false;
		}
		return true;
	}
}
